import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { TID } from "@/lib/testIds";

const HERO_BG =
  "https://images.unsplash.com/photo-1597603102823-8a6c796b64e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwyfHxiYXNrZXRiYWxsJTIwaG9vcCUyMG91dGRvb3IlMjBkcmFtYXRpYyUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4NTQ2Njc3MXww&ixlib=rb-4.1.0&q=85";

const line = {
  initial: { y: "110%" },
  animate: (i) => ({
    y: "0%",
    transition: {
      duration: 1.05,
      ease: [0.22, 1, 0.36, 1],
      delay: 0.55 + i * 0.09,
    },
  }),
};

const fade = {
  initial: { opacity: 0, y: 12 },
  animate: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: 1.4 + i * 0.08, ease: "easeOut" },
  }),
};

export const KineticHero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid={TID.hero.root}
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -top-20"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#060b14]/40 via-[#060b14]/70 to-[#060b14]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(42,95,219,0.35),transparent_60%)]" />
      </motion.div>

      {/* Top meta bar */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="absolute top-[88px] left-6 md:left-12 right-6 md:right-12 z-10 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.3em] text-white/60"
      >
        <span>Est. 2026 — Eastern Suburbs, Sydney</span>
      </motion.div>

      {/* Hero copy */}
      <motion.div
        style={{ y: textY, opacity }}
        className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-12 pt-[36vh] md:pt-[30vh] pb-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.6 }}
          className="chapter-num mb-8"
        >
          ⟶ Train · Believe · Achieve
        </motion.div>

        <h1 className="font-display uppercase leading-[0.85] text-[16vw] md:text-[11vw] lg:text-[10.5vw] tracking-[-0.02em]">
          <span className="reveal-mask">
            <motion.span
              custom={0}
              variants={line}
              initial="initial"
              animate="animate"
              className="block"
            >
              Sharpen
            </motion.span>
          </span>
          <span className="reveal-mask">
            <motion.span
              custom={1}
              variants={line}
              initial="initial"
              animate="animate"
              className="block"
            >
              the <span className="text-[#2a5fdb]">next</span>
            </motion.span>
          </span>
          <span className="reveal-mask">
            <motion.span
              custom={2}
              variants={line}
              initial="initial"
              animate="animate"
              className="block"
            >
              generation.
            </motion.span>
          </span>
        </h1>

        <div className="mt-14 grid grid-cols-12 gap-6 items-end">
          <motion.p
            variants={fade}
            custom={0}
            initial="initial"
            animate="animate"
            className="col-span-12 md:col-span-5 text-base md:text-lg text-white/80 leading-relaxed"
          >
            Coast 2 Coast is a private basketball academy built for players
            aged up to 18 across Sydney&apos;s Eastern Suburbs. One coach.
            Every rep intentional. Fundamentals, footwork and belief —
            built to travel with them for life.
          </motion.p>

          <motion.div
            variants={fade}
            custom={1}
            initial="initial"
            animate="animate"
            className="col-span-12 md:col-span-4 md:col-start-8 flex flex-wrap gap-3"
          >
            <a
              href="/book"
              data-testid={TID.hero.ctaPrimary}
              className="btn-sharp"
            >
              Book a session
              <span aria-hidden>→</span>
            </a>
            <a
              href="#philosophy"
              data-testid={TID.hero.ctaSecondary}
              className="btn-ghost"
            >
              The method
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Bottom scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-6 left-6 md:left-12 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 flex items-center gap-3"
      >
        <span className="w-6 h-px bg-white/40" />
        Join the family
      </motion.div>
    </section>
  );
};
